(() => {
  if (window.__newDesignJudgeMeInitialized) return;
  window.__newDesignJudgeMeInitialized = true;

  const widgetSelector = '#judgeme_product_reviews';

  const formatReviewDate = (timestamp) => {
    if (timestamp.dataset.newDesignDate === 'true') return;

    /* data-content is Judge.me's own ISO-ish timestamp, but it is missing on
       some reviews — those render a plain "MM/DD/YYYY", so fall back to the
       visible text rather than leaving the raw format on the page. */
    const rawDate = timestamp.dataset.content || timestamp.textContent.trim();
    if (!rawDate) return;

    /* Dates with no timezone ("MM/DD/YYYY", "July 20, 2026") are read as local
       midnight while the formatter below prints UTC — west of Greenwich that
       lands on the previous day. Pin those to UTC before formatting. */
    const slashed = rawDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    let parsedDate;
    if (slashed) {
      parsedDate = new Date(Date.UTC(Number(slashed[3]), Number(slashed[1]) - 1, Number(slashed[2])));
    } else if (timestamp.dataset.content) {
      parsedDate = new Date(rawDate.replace(' UTC', 'Z').replace(' ', 'T'));
    } else {
      /* Already-formatted text: re-read it as UTC so it survives unchanged. */
      const local = new Date(rawDate);
      if (Number.isNaN(local.getTime())) return;
      parsedDate = new Date(
        Date.UTC(local.getFullYear(), local.getMonth(), local.getDate())
      );
    }
    if (Number.isNaN(parsedDate.getTime())) return;

    timestamp.textContent = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(parsedDate);
    timestamp.dataset.newDesignDate = 'true';
  };

  /* Judge.me prefixes the shop's reply with ">> ", which the design doesn't
     show. The marker sits in its own text node inside .jdgm-rev__replier-
     wrapper, ahead of the <b> holding the shop name, so the whole reply is
     walked rather than just its first child.

     No done-flag here: the reply is often still being filled in when this
     first runs, and a flag would lock in that empty pass. Stripping is
     idempotent — once the ">>" is gone there is nothing left to match. */
  const stripReplyMarker = (reply) => {
    const walker = document.createTreeWalker(reply, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (!node.textContent.trim()) continue;
      node.textContent = node.textContent.replace(/^\s*>>\s*/, '');
      return;
    }
  };

  /* Judge.me's transparency badge is hidden by the stylesheet and rebuilt here.
     Theirs puts its label in a ::before that their CSS keeps refilling and
     inlines its own padding/font-size on the element, so it could not be
     restyled to the design — the label kept forcing the badge wider. Building
     our own is simpler than fighting that. */
  const replaceBadge = (badge) => {
    if (badge.dataset.newDesignBadge === 'true') return;
    badge.dataset.newDesignBadge = 'true';

    const wrapper = badge.parentElement;
    if (!wrapper || wrapper.querySelector(':scope > .new-design-jdgm-badge')) return;

    /* Reviews left by shop customers are labelled differently. The type string
       is spelled several ways ("..._from_store_visitor", "..._via_store_
       invitation"), so match on a substring rather than a fixed value. */
    const badgeType = badge.dataset.badgeType || '';
    const ownBadge = document.createElement('span');
    ownBadge.className = 'new-design-jdgm-badge';
    ownBadge.textContent = badgeType.includes('store') ? 'Verified by Shop' : 'Verified';
    wrapper.appendChild(ownBadge);
  };

  /* With no reviews the design drops the two-column layout entirely: one
     centred column with an outlined star, a line of copy and the write button.
     Judge.me's own empty widget still renders the rating summary and an empty
     histogram, so the state is flagged on the widget and the stylesheet hides
     those; the star and the caption are built here.

     The count comes from Judge.me's own data-number-of-reviews — the same
     attribute set that data-average-rating is read from above. Falls back to
     counting rendered reviews on the off chance the attribute is missing, so
     a populated widget is never mistaken for an empty one. */
  const renderEmptyState = (widget) => {
    const live = widget.querySelector('.jdgm-rev-widg:not(.new-design-jdgm-frozen)');
    if (!live) return;

    const declared = Number(live.dataset.numberOfReviews);
    const rendered = live.querySelectorAll('.jdgm-rev').length;
    const empty = Number.isFinite(declared) && live.dataset.numberOfReviews !== undefined
      ? declared === 0
      : rendered === 0;

    live.classList.toggle('new-design-jdgm-empty', empty);
    if (!empty) {
      live.querySelector(':scope > .new-design-jdgm-empty-state')?.remove();
      return;
    }

    /* Judge.me puts "Be the first to write a review" in the subtitle slot; the
       design reads "No reviews yet" there and saves that line for the empty
       state's own caption below. */
    const subtitle = live.querySelector('.jdgm-rev-widg__summary-text');
    if (subtitle && subtitle.textContent.trim() !== 'No reviews yet') {
      subtitle.textContent = 'No reviews yet';
    }

    /* The write button ships inside the header, above where the design wants
       it. It is moved on every pass, not just when the state is first built:
       Judge.me re-renders the widget and puts its own copy back in the header,
       which left the button sitting above the star again. */
    const actions = live.querySelector('.jdgm-widget-actions-wrapper');

    const existing = live.querySelector(':scope > .new-design-jdgm-empty-state');
    if (existing) {
      if (actions && actions.previousElementSibling !== existing) existing.after(actions);
      return;
    }

    const state = document.createElement('div');
    state.className = 'new-design-jdgm-empty-state';

    const star = document.createElement('span');
    star.className = 'new-design-jdgm-empty-state__star';

    const caption = document.createElement('p');
    caption.className = 'new-design-jdgm-empty-state__caption';
    caption.textContent = 'Be the first to write a review';

    state.append(star, caption);

    /* Straight after the header, so the design's order holds: title, star,
       caption, button. */
    const header = live.querySelector('.jdgm-rev-widg__header');
    if (header) header.after(state);
    else live.prepend(state);

    if (actions) state.after(actions);
  };

  const enhanceWidget = () => {
    const widget = document.querySelector(widgetSelector);
    if (!widget) {
      /* The open-flag hides the whole widget, so it must never outlive it —
         nor may the backdrop or the lifted ancestors. */
      document.body.classList.remove('new-design-jdgm-form-open');
      syncFormBackdrop(null, false);
      return;
    }

    const title = widget.querySelector('.jdgm-rev-widg__title');
    if (title && title.textContent.trim() !== 'Customer rating') {
      title.textContent = 'Customer rating';
    }

    const average = widget.querySelector('.jdgm-rev-widg__summary-average');
    const rating = Number(widget.querySelector('.jdgm-rev-widg')?.dataset.averageRating);
    if (average && Number.isFinite(rating)) {
      const formattedRating = rating.toFixed(1);
      if (average.textContent.trim() !== formattedRating) average.textContent = formattedRating;
    }

    const reviewsBody = widget.querySelector('.jdgm-rev-widg:not(.new-design-jdgm-frozen) .jdgm-rev-widg__body');
    if (reviewsBody && !reviewsBody.querySelector(':scope > .new-design-jdgm-reviews-title')) {
      const reviewsTitle = document.createElement('h2');
      reviewsTitle.className = 'new-design-jdgm-reviews-title';
      reviewsTitle.textContent = 'Reviews';
      reviewsBody.prepend(reviewsTitle);
    }

    renderEmptyState(widget);

    /* Scoped away from the drawer's frozen snapshot: it is meant to stay
       exactly as captured, and re-processing it would let the observer keep
       rewriting what the page shows. */
    const notFrozen = ':not(.new-design-jdgm-frozen) ';
    widget.querySelectorAll(`.jdgm-rev-widg${notFrozen}.jdgm-rev__timestamp`).forEach(formatReviewDate);
    widget
      .querySelectorAll(`.jdgm-rev-widg${notFrozen}.jdgm-rev__transparency-badge`)
      .forEach(replaceBadge);
    widget.querySelectorAll(`.jdgm-rev-widg${notFrozen}.jdgm-rev__reply`).forEach(stripReplyMarker);
    bindDrawerTrigger(widget);

    /* The observer fires on every re-render, including ones the drawer did not
       start. Re-hide the live widget so a rebuild can never expose it. */
    if (frozenList) hideLiveWidget(widget);

    bindAttachmentViewer(widget);
    bindWriteReviewReset(widget);
    syncReviewFormState(widget);
  };


  /* The review form is styled as a centred modal. Judge.me renders it inside
     the widget, where ancestor stacking contexts kept the review list painting
     over it however high its z-index went — so it is moved to <body>, the same
     fix the image viewer needed. Judge.me toggles its visibility rather than
     firing an event, so the state is read on every render. */
  const syncReviewFormState = (widget) => {
    /* Left where Judge.me put it: moving the form to <body> took it out of the
       tree the app manages, and it stopped opening at all. The stacking is
       handled in CSS instead — the rest of the widget is hidden while the form
       is up, which is exactly why this flag must be cleared reliably. */
    const form = widget.querySelector('.jdgm-form-wrapper');

    /* No early return when the form is missing: Judge.me removes it from the
       DOM on submit and on cancel, and bailing out left the open-flag set — the
       widget stayed hidden and the page went blank.

       offsetParent is always null for a fixed element, so visibility is read
       from the box itself; a hidden form measures 0x0. */
    const open = !!form && form.getBoundingClientRect().height > 0;
    document.body.classList.toggle('new-design-jdgm-form-open', open);
    syncFormBackdrop(widget, open);

    /* Re-applied on every render: validation re-draws the form, and the app
       puts its cancel control back in the actions row each time. */
    if (form) moveCancelToTitle(form);

    /* The form is closed behind Judge.me's back, so the app still believes it
       is open and re-labels its trigger "Cancel review" on every render. The
       label is corrected here rather than only in closeReviewForm, which the
       app would undo on its next pass. Only the wording is ours — the click
       still opens the form, as it always did. */
    const trigger = widget.querySelector('.jdgm-write-rev-link');
    if (trigger && !open && trigger.textContent.trim() !== 'Write a review') {
      trigger.textContent = 'Write a review';
    }
  };


  let formBackdrop = null;
  let liftedNodes = [];

  /* Ancestors of the form that create stacking contexts trap its z-index: the
     backdrop painted under the site header (visibly undimmed) and stray page
     layers above that context kept catching clicks meant for the inputs.
     While the form is open every such ancestor is lifted to the top layer;
     their previous inline values are put back on close. */
  const liftFormAncestors = (form) => {
    if (liftedNodes.length) return;
    for (let node = form.parentElement; node && node !== document.body; node = node.parentElement) {
      const style = getComputedStyle(node);
      const createsContext =
        (style.position !== 'static' && style.zIndex !== 'auto') ||
        style.isolation === 'isolate';
      if (!createsContext) continue;
      liftedNodes.push({ node, zIndex: node.style.zIndex });
      node.style.setProperty('z-index', '2147483000', 'important');
    }
  };

  const restoreFormAncestors = () => {
    liftedNodes.forEach(({ node, zIndex }) => {
      if (zIndex) node.style.zIndex = zIndex;
      else node.style.removeProperty('z-index');
    });
    liftedNodes = [];
  };

  /* The form is hidden directly rather than by clicking Judge.me's own "Cancel
     review". That control is an <a href="#judgeme_product_reviews">, so a
     synthetic click also jumped to the anchor — the page scrolled out from
     under the pointer and the next click landed somewhere else, which is what
     made the modal seem to need two presses.

     Judge.me itself toggles this form with an inline display (the wrapper
     reads style="display: none;" when closed), so setting it here leaves the
     form in exactly the state the app puts it in. */
  const closeReviewForm = () => {
    const form = document.querySelector(`${widgetSelector} .jdgm-form-wrapper`);
    if (form) form.style.display = 'none';

    /* Hiding the form behind Judge.me's back leaves the app thinking it is
       still open, so its trigger keeps the "Cancel review" label it swapped in.
       The label is the only part of that state the reader ever sees, so it is
       put back by hand rather than by driving the app's own cancel control —
       that control is an anchor, and clicking it scrolled the page out from
       under the pointer, which is what made the modal need two presses. */
    const trigger = document.querySelector(`${widgetSelector} .jdgm-write-rev-link`);
    if (trigger && trigger.textContent.trim() !== 'Write a review') {
      trigger.textContent = 'Write a review';
    }

    /* Torn down here rather than left to the observer: it only runs on the next
       frame, so the backdrop lingered over an already-closed form and swallowed
       the click that should have dismissed it. */
    document.body.classList.remove('new-design-jdgm-form-open');
    syncFormBackdrop(null, false);
  };

  function onFormKeydown(event) {
    if (event.key === 'Escape') closeReviewForm();
  }

  /* closeReviewForm hides the form with an inline display, and Judge.me's own
     "Write a review" handler does not always clear it — the form then stayed
     hidden and the trigger appeared dead on the second press. The style is
     dropped here so the app's own toggle takes over again. Bound once on the
     widget, since the trigger is replaced on every render. */
  const bindWriteReviewReset = (widget) => {
    if (widget.dataset.newDesignFormReset === 'true') return;
    widget.dataset.newDesignFormReset = 'true';

    widget.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest('.jdgm-write-rev-link')) return;

        /* Cleared a frame later, not in this handler. Judge.me still believes
           the form is open — we hid it behind the app's back — so its own
           toggle runs on this same click and collapses the form again. Clearing
           first meant the app had the last word and the card opened empty, with
           only the title, the stars and the button showing. Running afterwards
           puts the form back on top of whatever the app decided. */
        requestAnimationFrame(() => {
          widget
            .querySelectorAll('.jdgm-form-wrapper')
            .forEach((form) => form.style.removeProperty('display'));
        });
      },
      true
    );
  };

  /* The app's own "Cancel review" and "Submit Review" both dismiss the form.
     They are watched rather than wrapped: the click still reaches Judge.me
     untouched, and the backdrop is dropped on the next frame — by then the app
     has hidden the form, so nothing flashes. */
  function onWidgetFormDismiss(event) {
    /* The cross is handled here rather than left to the app: it is an
       <a href="#judgeme_product_reviews">, so letting the click through jumped
       to the anchor and scrolled the page. Closing it ourselves — the same path
       the backdrop and Esc take — keeps the modal and the dim in step. */
    if (event.target.closest('.jdgm-cancel-rev')) {
      event.preventDefault();
      event.stopPropagation();
      closeReviewForm();
      return;
    }

    if (!event.target.closest('.jdgm-submit-rev')) return;
    requestAnimationFrame(() => {
      document.body.classList.remove('new-design-jdgm-form-open');
      syncFormBackdrop(null, false);
    });
  }

  /* Anything outside the card dismisses the modal — the backdrop is only what
     the reader sees, not what has to receive the event. */
  function onDocumentFormClick(event) {
    if (event.target.closest('.jdgm-form-wrapper')) return;

    /* One gesture used to reach this handler twice; the marker keeps the
       second pass from re-opening what the first closed. The synthetic cancel
       click fired by closeReviewForm targets the card itself, so the check
       above already lets it through untouched. */
    if (event.__newDesignFormHandled) return;
    event.__newDesignFormHandled = true;

    event.preventDefault();
    event.stopPropagation();
    closeReviewForm();
  }

  /* Judge.me renders "Cancel review" in the actions row, next to Submit. The
     design puts it in the nav bar as a cross, and CSS alone could not get it
     there: the actions row is position: sticky, which makes it the containing
     block for anything absolutely placed inside it — so the cross landed on
     the Submit button rather than the top-right of the card.

     Moving it into the title bar, which is where the design has it anyway,
     makes the absolute placement resolve against the right box. */
  const moveCancelToTitle = (form) => {
    const cancel = form.querySelector('.jdgm-cancel-rev');
    const title = form.querySelector('.jdgm-form__title');
    if (!cancel || !title || cancel.parentElement === title) return;
    title.appendChild(cancel);
  };

  /* A real element rather than the card's spread box-shadow, which paints the
     dim but cannot receive clicks — and the design closes the modal when the
     dim is tapped. */
  const syncFormBackdrop = (widget, open) => {
    if (!open) {
      formBackdrop?.remove();
      formBackdrop = null;
      restoreFormAncestors();
      document.removeEventListener('keydown', onFormKeydown);
      document.removeEventListener('click', onDocumentFormClick, true);
      document
        .querySelector(widgetSelector)
        ?.removeEventListener('click', onWidgetFormDismiss, true);
      return;
    }
    if (formBackdrop?.isConnected) return;

    formBackdrop = document.createElement('div');
    formBackdrop.className = 'new-design-jdgm-form-backdrop';

    /* The dismiss click is caught on the document in the capture phase rather
       than on the backdrop itself: DevTools showed the backdrop as the element
       under the cursor, yet a listener bound to it never fired — something
       between them was swallowing the event. Capturing at the root sees the
       click before anything can stop it, and anything inside the card is let
       through untouched.

       Bound on the next frame so the very click that opened the form cannot
       reach it and close the modal immediately. */
    requestAnimationFrame(() => {
      if (formBackdrop?.isConnected) {
        document.addEventListener('click', onDocumentFormClick, true);
      }
    });

    /* Judge.me's own controls close the form without going through
       closeReviewForm, and the observer does not always fire afterwards — the
       backdrop then stayed over a form that was already gone. Watching the
       widget for those clicks tears it down on the same gesture. */
    widget.addEventListener('click', onWidgetFormDismiss, true);

    /* Inserted right before the form, not in <body>: the form's z-index only
       counts inside the widget's stacking context, so a backdrop mounted
       outside it would paint over the card instead of behind it. */
    const form = widget.querySelector('.jdgm-form-wrapper');
    if (form) {
      form.before(formBackdrop);
      liftFormAncestors(form);
      moveCancelToTitle(form);
      /* The card scrolls internally and keeps its offset between openings, so
         a reopened form started part-way down. */
      form.scrollTop = 0;
    }

    document.addEventListener('keydown', onFormKeydown);
  };

  /* The clicked attachment's full-size file. The <img> is only the 64px
     thumbnail — its src blown up looked soft and small next to Judge.me's own
     viewer, so the <a> around it is preferred. */
  const fullSize = (node) =>
    (node.matches('a') ? node : node.closest('a'))?.getAttribute('href') || null;

  /* Closing the drawer restores the first page from clones, and cloneNode does
     not carry Judge.me's lightbox handler — so afterwards a click on a photo
     fell through to its href and opened the raw file in a new tab. This catches
     those clicks on the widget and shows the image in our own viewer. Bound
     once; the guard below survives the widget being re-rendered. */
  const bindAttachmentViewer = (widget) => {
    if (widget.dataset.newDesignViewer === 'true') return;
    widget.dataset.newDesignViewer = 'true';

    widget.addEventListener('click', (event) => {
      const link = event.target.closest('.jdgm-rev__pic-link, .jdgm-rev__pic-img');
      /* Judge.me's own handler is still attached to nodes it rendered itself;
         only the restored clones are marked, and only those are taken over. */
      if (!link || !link.closest('[data-new-design-restored]')) return;

      const image = link.matches('img') ? link : link.querySelector('img');
      const source = image?.getAttribute('src') || image?.getAttribute('data-src');
      if (!source) return;

      event.preventDefault();
      openImageViewer(link.closest('.jdgm-rev__pics'), fullSize(link) || source);
    });
  };

  /* ---- "See all reviews" drawer ----------------------------------------
     The design opens the full review list in a panel on the right instead of
     navigating away. Judge.me paginates its widget, so the drawer starts with
     the reviews already on the page and pulls in the next page each time the
     reader nears the bottom — endless scrolling, as drawn. */

  let drawer = null;
  let drawerList = null;
  let loadMoreButton = null;
  let loadingPage = false;
  let suppressDrawerTrigger = false;

  /* idle = more to fetch, loading = request in flight, done = end of the list */
  const setLoadMoreState = (state) => {
    if (!loadMoreButton) return;
    loadMoreButton.hidden = state === 'done';
    loadMoreButton.disabled = state === 'loading';
    loadMoreButton.textContent = state === 'loading' ? 'Loading…' : 'Load more reviews';
  };

  const closeDrawer = () => {
    if (!drawer) return;

    /* Paging ran through the widget's own controls, so the page's list was
       replaced along the way. Put the first page back before the page is
       visible again — restoring on every load would have the list visibly
       rebuild itself behind the drawer. */
    const widget = document.querySelector(widgetSelector);
    if (widget) unfreezePageReviews(widget);

    drawer.hidden = true;
    document.body.classList.remove('new-design-jdgm-drawer-open');
    document.removeEventListener('keydown', onDrawerKeydown);
  };

  function onDrawerKeydown(event) {
    if (event.key === 'Escape') closeDrawer();
  }

  const onDrawerScroll = () => {
    if (!drawerList || loadingPage || !loadMoreButton || loadMoreButton.hidden) return;
    const { scrollTop, scrollHeight, clientHeight } = drawerList;
    if (scrollHeight - (scrollTop + clientHeight) < 600) loadNextPage();
  };

  /* Snapshot of the reviews the product page shows on load, taken before the
     drawer starts paging the widget. */
  let pageReviews = null;
  let paginateMarkup = null;

  const capturePageReviews = (widget) => {
    const body = widget.querySelector('.jdgm-rev-widg:not(.new-design-jdgm-frozen) .jdgm-rev-widg__body');
    if (!body || pageReviews) return;
    pageReviews = [...body.querySelectorAll('.jdgm-rev')].map((review) => review.cloneNode(true));
    paginateMarkup = body.querySelector('.jdgm-paginate')?.innerHTML ?? null;
  };

  /* The frozen stand-in shown while the drawer is open. */
  let frozenList = null;
  let snapshotWatcher = null;

  /* Kept unrenamed in the snapshot — see disarm() in freezePageReviews. */
  const STAR_CLASSES = new Set(['jdgm-star', 'jdgm--on', 'jdgm--off', 'jdgm--half']);

  /* Judge.me needs its live widget in the document to page through it, but the
     reader must not watch it advance. Rather than freezing pieces of the widget
     — which failed because the app replaces whole nodes as it pages, taking any
     marker we set with them — the ENTIRE widget is snapshotted once and the
     copy is what the page shows. The live widget goes off-screen wholesale, so
     nothing the app does to it can surface. */
  const freezePageReviews = (widget) => {
    const live = widget.querySelector('.jdgm-rev-widg:not(.new-design-jdgm-frozen)');
    if (!live || frozenList) return;

    frozenList = live.cloneNode(true);
    frozenList.classList.add('new-design-jdgm-frozen');

    /* A dead copy: no controls, no ids that would collide with the live tree. */
    frozenList.removeAttribute('id');
    frozenList.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    frozenList.querySelectorAll('a[href]').forEach((link) => link.removeAttribute('href'));
    frozenList.querySelectorAll('button').forEach((button) => {
      button.disabled = true;
    });
    frozenList.querySelectorAll('.new-design-jdgm-drawer').forEach((node) => node.remove());

    /* Judge.me's pagination is swapped for a plain stand-in button. Its real
       "See all reviews" styling is keyed to the jdgm- classes renamed below, so
       the copy would otherwise show the app's raw page numbers ("1 2") — and
       the control does nothing while the drawer it opens is already open. */
    frozenList.querySelectorAll('.jdgm-paginate').forEach((node) => {
      const standIn = document.createElement('div');
      standIn.className = 'new-design-jdgm-frozen-paginate';
      standIn.textContent = 'See all reviews';
      node.replaceWith(standIn);
    });

    /* Judge.me finds its nodes by their data-* hooks AND their jdgm-* classes,
       and the copy carried an identical set — the app kept rewriting the
       snapshot as it paged, emptying it from five reviews down to three.
       Both are renamed so the copy matches none of the app's selectors, while
       the stylesheet still reaches it: every rule is duplicated for the
       "jdgmfrozen-" prefix at the end of new-design-judgeme.css. */
    const disarm = (node) => {
      [...node.attributes].forEach((attr) => {
        if (attr.name.startsWith('data-jdgm') || attr.name === 'data-review-id') {
          node.removeAttribute(attr.name);
        }
      });
      if (!node.className || typeof node.className !== 'string') return;
      /* Only Judge.me's own classes, matched from the start of each class name:
         a plain /\bjdgm-/ also fired mid-word and mangled our own
         "new-design-jdgm-frozen-paginate" into a class no rule matches.

         The star classes are left alone — the glyph itself comes from a
         ::before rule in Judge.me's stylesheet keyed to .jdgm-star, so renaming
         them emptied every rating in the snapshot. They are display-only and
         carry no hooks the app uses to find reviews. */
      node.className = node.className
        .split(/\s+/)
        .map((name) =>
          name.startsWith('jdgm-') && !STAR_CLASSES.has(name) ? `jdgmfrozen-${name.slice(5)}` : name
        )
        .join(' ');
    };

    disarm(frozenList);
    frozenList.querySelectorAll('*').forEach(disarm);
    frozenList.classList.add('new-design-jdgm-frozen');

    /* Kept inside #judgeme_product_reviews so the stylesheet, which is written
       against that id, still reaches it. The app is stopped from rewriting the
       copy by the data-* strip above rather than by moving it out of reach —
       relocating it cost the snapshot every style rule it depends on. */
    live.after(frozenList);


    /* Hiding is done with inline styles rather than a stylesheet rule. Every
       CSS-based attempt so far lost: the app re-creates nodes as it pages, and
       a rule keyed on a class can be out-specified or land on a node that no
       longer exists. An inline style beats any rule and is re-applied below on
       every load, so it survives whatever the app rebuilds. */
    hideLiveWidget(widget);

  };

  /* Applied to the live widget for as long as the drawer is open. */
  const hideLiveWidget = (widget) => {
    const live = widget.querySelector('.jdgm-rev-widg:not(.new-design-jdgm-frozen)');
    if (!live) return;
    live.style.setProperty('position', 'absolute', 'important');
    live.style.setProperty('width', '1px', 'important');
    live.style.setProperty('height', '1px', 'important');
    live.style.setProperty('overflow', 'hidden', 'important');
    live.style.setProperty('opacity', '0', 'important');
    live.style.setProperty('pointer-events', 'none', 'important');
    live.style.setProperty('clip-path', 'inset(50%)', 'important');
  };

  const unfreezePageReviews = (widget) => {
    if (!frozenList) return;

    snapshotWatcher?.disconnect();
    snapshotWatcher = null;
    frozenList.remove();
    frozenList = null;

    const live = widget.querySelector('.jdgm-rev-widg:not(.new-design-jdgm-frozen)');
    ['position', 'width', 'height', 'overflow', 'opacity', 'pointer-events', 'clip-path'].forEach(
      (prop) => live?.style.removeProperty(prop)
    );
    const body = live?.querySelector('.jdgm-rev-widg__body');
    if (!body || !pageReviews) return;

    /* The live widget was paged while it was hidden, so it is showing the last
       page fetched. Put the first page back, since that is what the product
       page is meant to show. */
    body.querySelectorAll('.jdgm-rev').forEach((review) => review.remove());

    /* Back into .jdgm-rev-widg__reviews, not straight into the body: that list
       carries the 16px side padding and the 24px gap between reviews, so
       re-inserting a level up left the restored page without either. */
    const list = body.querySelector('.jdgm-rev-widg__reviews');
    const paginate = body.querySelector('.jdgm-paginate');
    pageReviews.forEach((review) => {
      const copy = review.cloneNode(true);
      /* Marks the clone as ours: its attachments have lost Judge.me's lightbox
         handler, so bindAttachmentViewer takes their clicks over. */
      copy.dataset.newDesignRestored = 'true';
      if (list) list.appendChild(copy);
      else if (paginate) body.insertBefore(copy, paginate);
      else body.appendChild(copy);
    });

    /* Paging leaves the widget's next-page link disabled or removed, which
       would take "See all reviews" with it. The drawer still has more to show,
       so the link is put back the way the first page had it. */
    if (paginate && paginateMarkup !== null) paginate.innerHTML = paginateMarkup;
  };

  /* Judge.me renders its widget client-side, so fetching the next page's HTML
     returns a document with no reviews in it — the app would still have to run
     to fill it. Instead the widget's own pagination is driven: clicking its
     next-page link makes the app replace the on-page list, and the freshly
     rendered reviews are moved into the drawer.

     Waits for .jdgm-rev nodes that were not there before, since the swap
     happens whenever the app's request comes back. */
  const waitForNewReviews = (widget, known) =>
    new Promise((resolve) => {
      const deadline = 8000;
      const started = performance.now();

      const check = () => {
        const fresh = [...widget.querySelectorAll(".jdgm-rev-widg:not(.new-design-jdgm-frozen) .jdgm-rev-widg__body .jdgm-rev")].filter(
          (review) => !known.has(reviewKey(review))
        );
        if (fresh.length) return resolve(fresh);
        if (performance.now() - started > deadline) return resolve([]);
        setTimeout(check, 120);
      };

      check();
    });

  /* Reviews carry no id, so identity is the author + date + body text. */
  const reviewKey = (review) =>
    [
      review.querySelector('.jdgm-rev__author')?.textContent,
      review.querySelector('.jdgm-rev__timestamp')?.textContent,
      review.querySelector('.jdgm-rev__body')?.textContent,
    ].join('|');

  const loadNextPage = async () => {
    const widget = document.querySelector(widgetSelector);
    if (loadingPage || !drawerList || !widget) return;

    const trigger = widget.querySelector('.jdgm-rev-widg:not(.new-design-jdgm-frozen) .jdgm-paginate__next-page');
    if (!trigger || trigger.classList.contains('jdgm-disabled')) {
      setLoadMoreState('done');
      return;
    }

    loadingPage = true;
    setLoadMoreState('loading');

    const known = new Set(
      [...widget.querySelectorAll(".jdgm-rev-widg:not(.new-design-jdgm-frozen) .jdgm-rev-widg__body .jdgm-rev")].map(reviewKey)
    );

    /* The capture-phase guard on the widget swallows clicks on this link so the
       page behind the drawer stays put — bypass it by calling the app's own
       handler path directly rather than dispatching a click. */
    suppressDrawerTrigger = true;
    trigger.click();
    suppressDrawerTrigger = false;

    const fresh = await waitForNewReviews(widget, known);

    /* The app may have rebuilt the widget node during this load, which drops
       the inline hide and can detach the snapshot. Re-apply both. */
    hideLiveWidget(widget);
    if (frozenList && !frozenList.isConnected) {
      widget.querySelector('.jdgm-rev-widg:not(.new-design-jdgm-frozen)')?.after(frozenList);
    }


    fresh.forEach((review) => {
      review.querySelectorAll('.jdgm-rev__timestamp').forEach(formatReviewDate);
      review.querySelectorAll('.jdgm-rev__transparency-badge').forEach(replaceBadge);
      review.querySelectorAll('.jdgm-rev__reply').forEach(stripReplyMarker);
      drawerList.appendChild(review.cloneNode(true));
    });

    loadingPage = false;

    const next = widget.querySelector('.jdgm-rev-widg:not(.new-design-jdgm-frozen) .jdgm-paginate__next-page');
    setLoadMoreState(fresh.length && next && !next.classList.contains('jdgm-disabled') ? 'idle' : 'done');
  };

  /* Minimal stand-in for Judge.me's lightbox, used only for attachments that
     came in through paging — see the drawer's click handler for why. Steps
     through the photos of the review it was opened from. */
  const openImageViewer = (pics, source) => {
    /* The <a> points at the full-size file; the <img> inside it is the 64px
       thumbnail. Reading the img's src showed that thumbnail blown up, which is
       why the photo looked smaller and softer than in Judge.me's own viewer. */
    const sources = [...(pics?.querySelectorAll('.jdgm-rev__pic-link, .jdgm-rev__pic-img') ?? [])]
      .map((node) =>
        node.matches('a')
          ? node.getAttribute('href')
          : node.closest('a')?.getAttribute('href') ||
            node.getAttribute('src') ||
            node.getAttribute('data-src')
      )
      .filter(Boolean)
      .filter((value, index, all) => all.indexOf(value) === index);
    if (!sources.length) return;

    let index = Math.max(0, sources.indexOf(source));

    const viewer = document.createElement('div');
    viewer.className = 'new-design-jdgm-viewer';

    const image = document.createElement('img');
    image.className = 'new-design-jdgm-viewer__image';
    image.alt = '';

    const counter = document.createElement('p');
    counter.className = 'new-design-jdgm-viewer__counter';

    const show = () => {
      image.src = sources[index];
      counter.textContent = `${index + 1} / ${sources.length}`;
      counter.hidden = sources.length < 2;
    };

    const step = (by) => {
      index = (index + by + sources.length) % sources.length;
      show();
    };

    const close = () => {
      viewer.remove();
      document.removeEventListener('keydown', onKey);
    };

    function onKey(event) {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    }

    const button = (className, label, onClick) => {
      const element = document.createElement('button');
      element.type = 'button';
      element.className = className;
      element.setAttribute('aria-label', label);
      element.addEventListener('click', (event) => {
        event.stopPropagation();
        onClick();
      });
      return element;
    };

    viewer.append(
      button('new-design-jdgm-viewer__close', 'Close image', close),
      image,
      counter
    );

    if (sources.length > 1) {
      viewer.append(
        button('new-design-jdgm-viewer__nav new-design-jdgm-viewer__nav--prev', 'Previous image', () =>
          step(-1)
        ),
        button('new-design-jdgm-viewer__nav new-design-jdgm-viewer__nav--next', 'Next image', () =>
          step(1)
        )
      );
    }

    viewer.addEventListener('click', (event) => {
      if (event.target === viewer) close();
    });
    document.addEventListener('keydown', onKey);

    show();
    /* On <body>, not inside the drawer: the drawer is hidden once closed, and
       anything mounted in it would be hidden too — which is why clicking a
       restored photo appeared to do nothing. */
    document.body.appendChild(viewer);
  };

  const buildDrawer = (widget) => {
    drawer = document.createElement('div');
    drawer.className = 'new-design-jdgm-drawer';
    drawer.hidden = true;

    const overlay = document.createElement('div');
    overlay.className = 'new-design-jdgm-drawer__overlay';
    overlay.addEventListener('click', closeDrawer);

    const panel = document.createElement('div');
    panel.className = 'new-design-jdgm-drawer__panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', 'Product reviews');

    const header = document.createElement('div');
    header.className = 'new-design-jdgm-drawer__header';

    const heading = document.createElement('h2');
    heading.className = 'new-design-jdgm-drawer__title';
    heading.textContent = 'Product reviews';

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'new-design-jdgm-drawer__close';
    close.setAttribute('aria-label', 'Close reviews');
    close.addEventListener('click', closeDrawer);

    drawerList = document.createElement('div');
    drawerList.className = 'new-design-jdgm-drawer__list';
    drawerList.addEventListener('scroll', onDrawerScroll, { passive: true });

    /* Attachments in the drawer are clones, so Judge.me's lightbox handler did
       not come with them and the bare href opened the image in a new tab. The
       click is forwarded to the matching thumbnail in the live widget, which
       still has the handler — that opens the viewer as it does on the page. */
    drawerList.addEventListener('click', (event) => {
      const link = event.target.closest('.jdgm-rev__pic-link, .jdgm-rev__pic-img');
      if (!link) return;

      event.preventDefault();

      const image = link.matches('img') ? link : link.querySelector('img');
      const source = image?.getAttribute('src') || image?.getAttribute('data-src');
      if (!source) return;

      const widget = document.querySelector(widgetSelector);
      const original = [
        ...(widget?.querySelectorAll(
          '.jdgm-rev-widg:not(.new-design-jdgm-frozen) .jdgm-rev__pic-img'
        ) ?? []),
      ].find((node) => (node.getAttribute('src') || node.getAttribute('data-src')) === source);

      if (original) {
        original.click();
        return;
      }

      /* Reviews pulled in by paging have no counterpart on the page, so there
         is no handler to borrow — show the full image in the drawer instead of
         falling through to the href and leaving the site. */
      openImageViewer(link.closest('.jdgm-rev__pics'), fullSize(link) || source);
    });

    /* Scrolling triggers the next page on its own; the button is the fallback
       for when the list is too short to scroll, and gives the reader something
       to act on rather than waiting for an invisible threshold. */
    loadMoreButton = document.createElement('button');
    loadMoreButton.type = 'button';
    loadMoreButton.className = 'new-design-jdgm-drawer__more';
    loadMoreButton.textContent = 'Load more reviews';
    loadMoreButton.addEventListener('click', loadNextPage);

    header.append(heading, close);
    panel.append(header, drawerList, loadMoreButton);
    drawer.append(overlay, panel);
    widget.appendChild(drawer);
  };

  const openDrawer = (widget) => {

    if (!drawer) buildDrawer(widget);
    capturePageReviews(widget);

    /* Always start from the captured first page, not from whatever is in the
       DOM — on a second open the widget may still be showing a later page. The
       clones are re-processed because the date formatter and badge builder mark
       the nodes they have handled, and those markers come across with the copy. */
    drawerList.replaceChildren();
    (pageReviews ?? []).forEach((review) => {
      const clone = review.cloneNode(true);
      clone.querySelectorAll('.jdgm-rev__timestamp').forEach((node) => {
        delete node.dataset.newDesignDate;
        formatReviewDate(node);
      });
      clone.querySelectorAll('.jdgm-rev__reply').forEach(stripReplyMarker);
      drawerList.appendChild(clone);
    });

    const next = widget.querySelector('.jdgm-rev-widg:not(.new-design-jdgm-frozen) .jdgm-paginate__next-page');
    setLoadMoreState(next && !next.classList.contains('jdgm-disabled') ? 'idle' : 'done');

    freezePageReviews(widget);

    drawer.hidden = false;
    document.body.classList.add('new-design-jdgm-drawer-open');
    document.addEventListener('keydown', onDrawerKeydown);
    drawer.querySelector('.new-design-jdgm-drawer__close').focus();
  };

  /* The pagination link is replaced by Judge.me on every render, so the click
     is caught on the widget rather than bound to the link itself. */
  const bindDrawerTrigger = (widget) => {
    if (widget.dataset.newDesignDrawer === 'true') return;
    widget.dataset.newDesignDrawer = 'true';

    /* Capture phase, and the event is stopped outright: Judge.me binds its own
       handler to this link that swaps the widget for the next page in place.
       preventDefault alone only cancels the navigation, so their handler still
       ran and the list behind the drawer advanced. */
    widget.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('.jdgm-paginate__next-page');
        if (!trigger || suppressDrawerTrigger) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        openDrawer(widget);
      },
      true
    );
  };

  let updateScheduled = false;
  const scheduleUpdate = () => {
    if (updateScheduled) return;
    updateScheduled = true;

    requestAnimationFrame(() => {
      updateScheduled = false;
      enhanceWidget();
    });
  };

  const observer = new MutationObserver(scheduleUpdate);

  const init = () => {
    enhanceWidget();
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
